import {PipelineStepStatusCode, PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {PipelineStep, PipelineStepData} from 'pipelines/PipelineTypes';
import {JSONValue} from 'util/Types';

/**
 * A partial PipelineStep for preview purposes. The sidebar skeleton's preview panel
 * assumes a real PipelineStep, but we haven't created the real step yet. This is
 * intentionally not the full PipelineStep type — callers use @ts-expect-error where they
 * pass it to APIs that require the full type.
 */
export type FakePipelineStep = Pick<
  PipelineStep,
  | 'id'
  | 'stepType'
  | 'stepVersion'
  | 'inputStepDatas'
  | 'outputStepDatas'
  | 'incomingEdges'
  | 'outgoingEdges'
  | 'statusCode'
  | 'statusMessage'
> & {
  config: Record<string, JSONValue> | null;
};

/**
 * Creates a fake PipelineStep for use in `RightSidebarSkeleton`'s preview system.
 * The preview panel uses `draftStep.inputStepDatas` and `draftStep.config` to build
 * preview requests, so these fields must be accurate.
 *
 * @param inputStepDatas - Input step data slots for the step. For step types with required
 *   inputs, callers should always provide this argument with the appropriate slots (even if
 *   rawDatas are empty) to avoid assertion errors in getInputStepDatasRecord. Defaults to `[]`
 *   which is valid for step types with no required inputs.
 */
export default function makeFakePipelineStep(
  stepType: PipelineStepType,
  stepVersion: number,
  config: Record<string, JSONValue> | null,
  inputStepDatas: Array<PipelineStepData> = []
): FakePipelineStep {
  return {
    id: `${stepType}-sidebar-draft`,
    stepType,
    stepVersion,
    config,
    inputStepDatas,
    outputStepDatas: [],
    incomingEdges: [],
    outgoingEdges: [],
    statusCode: PipelineStepStatusCode.WAITING,
    statusMessage: null,
  };
}
